import { useState, useEffect } from "react";

/**
 * useVariantSelection - Unified logic for ecom variant selection.
 * @param {Array} variants - List of variants (structure: must have id/variant_id, color, size, stock...)
 * @param {number|null} preferredVariantId - preferred variant id from URL/route params
 * @param {string} preferredColor - preferred color from URL/route params
 * @return {object}
 *   selectedVariant: the best current variant object, or fallback
 *   selectedColor: color
 *   selectedSize: size
 *   setSelectedColor: function
 *   setSelectedSize: function
 */
export default function useVariantSelection(variants, preferredVariantId, preferredColor) {
    const [selectedColor, setSelectedColor] = useState('');
    const [selectedSize, setSelectedSize] = useState('');

    useEffect(() => {
        let preColor = '';
        let preSize = '';
        let preVariant = null;
        if (preferredVariantId && variants.length > 0) {
            // Try to preselect requested variant
            preVariant = variants.find(v => Number(v.id || v.variant_id) === Number(preferredVariantId));
            if (preVariant) {
                preColor = preVariant.color || '';
                preSize = String(preVariant.size ?? '');
            }
        }
        if (!preVariant && variants.length > 0) {
            // Try by color from URL
            const validColors = Array.from(new Set(variants.map(v => v.color).filter(Boolean)));
            preColor = preferredColor && validColors.includes(preferredColor) ? preferredColor : validColors[0] || '';
            const filtered = variants.filter(v => v.color === preColor);
            // Pick first in-stock, else first
            const firstInStock = filtered.find(v => (v.stock ?? 0) > 0) || filtered[0] || variants[0];
            preSize = firstInStock ? String(firstInStock.size ?? '') : '';
            preVariant = firstInStock || filtered[0] || variants[0];
        }
        setSelectedColor(preColor);
        setSelectedSize(preSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variants, preferredVariantId, preferredColor]);

    // Helper to get currently selected variant
    const getSelectedVariant = () => {
        return variants.find(v => (
            (selectedColor ? v.color === selectedColor : true) &&
            (selectedSize ? String(v.size ?? '') === String(selectedSize) : true)
        )) || null;
    };

    // In signature with fallback - ensure always returns a valid variant as much as possible
    return {
        selectedVariant: getSelectedVariant(),
        selectedColor,
        setSelectedColor,
        selectedSize,
        setSelectedSize,
        allVariants: variants,
    };
}
